import { getTimeWithOffset } from '@dispatch/shared';
import { RelatedTask, TaskResponse } from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import {
  EMPTY_VALUE,
  ResolutionChip,
  dateColumn,
  tableStyleOverrides,
  textColumn,
  MUI_X_5_GRID_PROPS,
  useGridApiRefKeepingPage,
} from '@dispatch/web/ui';
import { Stack } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import {
  AssignedResourceChip,
  MethodOfInspectionChip,
} from '../../../list/components';
import { GeoStatusChip } from '../../../shared';
import { useNavigate } from 'react-router-dom';

type RelatedTasksProps = {
  taskData?: TaskResponse;
};

export const RelatedTasks = ({ taskData }: RelatedTasksProps) => {
  const apiRef = useGridApiRefKeepingPage();
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    textColumn({
      field: 'claimNumber',
      headerName: 'Claim Number',
      flex: 1,
      sortable: false,
    }),
    dateColumn(
      {
        field: 'appointment',
        headerName: 'Appointment',
        valueGetter: (_value, row) =>
          row?.appointment
            ? getTimeWithOffset(row?.timeZoneId, row?.appointment)
            : EMPTY_VALUE,
        flex: 1,
      },
      'MMM dd, yyyy | HH:mm'
    ),

    dateColumn(
      {
        field: 'taskReceived',
        headerName: 'Date Received',
        valueGetter: (_value, row) =>
          row?.taskReceived
            ? getTimeWithOffset(row?.timeZoneId, row?.taskReceived)
            : EMPTY_VALUE,
        flex: 1,
      },
      'MMM dd, yyyy | HH:mm'
    ),
    {
      field: 'assignedResourceId',
      headerName: 'Assigned Resource',
      renderCell: (params) => (
        <div>
          {!params.value && <p>-</p>}
          {params.value && (
            <AssignedResourceChip name={params.row.assignedResource} />
          )}
        </div>
      ),
      flex: 1,
      sortable: false,
    },
    {
      field: 'targetQueue',
      headerName: 'Status',
      renderCell: (params) => {
        return (
          <Stack direction="row" gap={1}>
            <ResolutionChip
              targetQueue={params.value}
              assignedResourceId={params.row.assignedResourceId}
              requiredResource={params.row.requiredResource}
            />
            <GeoStatusChip
              geoStatus={params.row.geoStatus}
              showIcon
              size="small"
            />
          </Stack>
        );
      },
      flex: 0.5,
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'methodOfInspection',
      headerName: 'Method of Inspection',
      renderCell: (params) => (
        <div style={{ padding: '0, 5px' }}>
          <MethodOfInspectionChip methodOfInspection={params.value} />
        </div>
      ),
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'taskType',
      headerName: 'Task Type',
      valueGetter: (value: RelatedTask['taskType']) =>
        value ? value.friendlyName : EMPTY_VALUE,
      flex: 1,
      sortable: false,
    },
  ];

  return (
    <div style={{ height: '500px' }}>
      <DataGridPro
        apiRef={apiRef}
        {...MUI_X_5_GRID_PROPS}
        disableVirtualization={isRunningInJest()}
        rows={taskData?.$relatedTasks || []}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No Related Tasks
            </Stack>
          ),
        }}
        columns={columns}
        sx={tableStyleOverrides}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          navigate(`/tasks/${row.id}`);
        }}
        hideFooter={isRunningInJest()}
        hideFooterSelectedRowCount={true}
        disableRowSelectionOnClick={true}
        pagination
        autoPageSize
      />
    </div>
  );
};
